import { useCallback, useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { useTranslate } from 'react-admin';
import {
  Card,
  CardContent,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { apiClient } from '../../utils/apiClient';
import type { ApiError } from '../../utils/apiClient';
import { StatusChip, CategoryChip, ConfidenceChip } from './reportDisplay';
import { formatCostUsd, formatLatency, formatTokens, modelLabel, mergeRuns } from './comparisonFormat';
import type { ComparisonResponse, ComparisonData, ComparisonRun } from './comparisonTypes';

interface ModelComparisonPanelProps {
  investigationId: string;
}

// Imperative handle so the parent (the detail) can trigger a refetch after a replay completes.
export interface ModelComparisonHandle {
  refetch: () => void;
}

// Label/value row inside a card body. `value` already formatted to N/A where missing.
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
      {children}
    </Box>
  </Box>
);

const RunCard = ({ run }: { run: ComparisonRun }) => {
  const translate = useTranslate();
  const na = translate('aiTroubleshooting.comparison.notAvailable', { _: 'N/A' });

  return (
    <Card
      variant="outlined"
      sx={{ flex: { md: '0 0 280px' }, minWidth: { xs: '100%', md: 280 }, maxWidth: { md: 340 } }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {modelLabel(run.model, na)}
          </Typography>
          {run.isOriginal && (
            <Chip
              size="small"
              color="primary"
              label={translate('aiTroubleshooting.comparison.original', { _: 'Original' })}
            />
          )}
        </Box>

        <Field label={translate('aiTroubleshooting.comparison.status', { _: 'Status' })}>
          <StatusChip translate={translate} status={run.status} />
        </Field>

        <Field label={translate('aiTroubleshooting.comparison.category', { _: 'Category' })}>
          {run.rootCauseCategory ? (
            <CategoryChip translate={translate} category={run.rootCauseCategory} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {na}
            </Typography>
          )}
        </Field>

        <Field label={translate('aiTroubleshooting.comparison.confidence', { _: 'Confidence' })}>
          {run.confidence ? (
            <ConfidenceChip translate={translate} confidence={run.confidence} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {na}
            </Typography>
          )}
        </Field>

        <Field label={translate('aiTroubleshooting.comparison.conclusion', { _: 'Conclusion' })}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {run.conclusionSummary ?? na}
          </Typography>
        </Field>

        <Field label={translate('aiTroubleshooting.comparison.tokens', { _: 'Tokens (in / out)' })}>
          <Typography variant="body2">
            {formatTokens(run.usage?.inputTokens, na)} / {formatTokens(run.usage?.outputTokens, na)}
          </Typography>
        </Field>

        <Field label={translate('aiTroubleshooting.comparison.cost', { _: 'Cost' })}>
          <Typography variant="body2">{formatCostUsd(run.usage?.costUsd, na)}</Typography>
        </Field>

        <Field label={translate('aiTroubleshooting.comparison.latency', { _: 'Latency' })}>
          <Typography variant="body2">{formatLatency(run.latencyMs, na)}</Typography>
        </Field>
      </CardContent>
    </Card>
  );
};

// Side-by-side comparison of every model run against one investigation (sub-plan 20). Loads
// GET /investigations/:id/comparison (sub-plan 17-B / REF-08 §3.2), merges { original, replays }
// into [original, ...replays], and renders one column per run. Gated upstream by the manage
// permission. Layout uses MUI v7 Box flexbox (no Grid).
export const ModelComparisonPanel = forwardRef<ModelComparisonHandle, ModelComparisonPanelProps>(
  ({ investigationId }, ref) => {
    const translate = useTranslate();
    const [data, setData] = useState<ComparisonData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notFound, setNotFound] = useState(false);

    const fetchComparison = useCallback(async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const res = await apiClient.get<ComparisonResponse>(
          `/api/backend/ai-troubleshooting/investigations/${investigationId}/comparison`,
        );
        setData(res.data);
      } catch (err) {
        const e = err as ApiError;
        if (e?.status === 404 || e?.errorCode === 'AI_INVESTIGATION_NOT_FOUND') {
          setNotFound(true);
        } else {
          setError(
            e?.message ||
              translate('aiTroubleshooting.comparison.loadFailed', {
                _: 'Failed to load the comparison.',
              }),
          );
        }
      } finally {
        setLoading(false);
      }
    }, [investigationId, translate]);

    useEffect(() => {
      fetchComparison();
    }, [fetchComparison]);

    useImperativeHandle(ref, () => ({ refetch: fetchComparison }), [fetchComparison]);

    const rows = data ? mergeRuns(data) : [];

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {translate('aiTroubleshooting.comparison.title', { _: 'Model comparison' })}
          </Typography>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {notFound && !loading && (
            <Alert severity="warning">
              {translate('aiTroubleshooting.comparison.notFound', {
                _: 'Comparison unavailable (investigation not found or out of scope).',
              })}
            </Alert>
          )}

          {error && !loading && !notFound && <Alert severity="error">{error}</Alert>}

          {data && !loading && !notFound && (
            <>
              {/* Shared symptom + anchor header (held constant across the A/B). */}
              {(data.symptomText || data.anchorType) && (
                <Box sx={{ mb: 2 }}>
                  {data.symptomText && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {translate('aiTroubleshooting.comparison.symptom', { _: 'Symptom' })}:
                      </Box>{' '}
                      {data.symptomText}
                    </Typography>
                  )}
                  {data.anchorType && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {translate('aiTroubleshooting.comparison.anchor', { _: 'Entity' })}:
                      </Box>{' '}
                      {translate(`aiTroubleshooting.anchorType.${data.anchorType}`, {
                        _: data.anchorType,
                      })}
                      {data.anchorId ? ` (${data.anchorId})` : ''}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Single-run hint (only the original, no replays yet). */}
              {data.replays.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {translate('aiTroubleshooting.comparison.singleRunHint', {
                    _: 'Run a replay with another model to compare.',
                  })}
                </Alert>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2,
                  overflowX: { md: 'auto' },
                  pb: 1,
                }}
              >
                {rows.map((run) => (
                  <RunCard key={run.investigationId} run={run} />
                ))}
              </Box>
            </>
          )}

          {/* The original always exists; a 404 covers a missing/out-of-scope id. This guards the
              theoretically-impossible "loaded but no original" shape. */}
          {data && !loading && !notFound && !data.original && (
            <Alert severity="warning">
              {translate('aiTroubleshooting.comparison.noRuns', { _: 'No runs to compare.' })}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  },
);

ModelComparisonPanel.displayName = 'ModelComparisonPanel';

export default ModelComparisonPanel;
